import { useState, useEffect, useCallback, useMemo } from "react";
import { AppLayout } from "../../components/AppLayout";
import { NotificationSettingsDialog } from "../../components/NotificationSettingsDialog";
import { WeeklySlotsEditor } from "../../components/WeeklySlotsEditor";
import { SchedulePeriods } from "../../components/SchedulePeriods";
import { slotsForDate } from "../../lib/schedule";
import { MonthCalendar } from "../../components/MonthCalendar";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import {
  CalendarDays, List, SlidersHorizontal, Plane, Save, Loader2, X,
  Clock, User, Building2, Ban, FileDown, BookText,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

function todayIso() { return new Date().toISOString().slice(0, 10); }
function fmtDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function MedicoDashboard() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [appts, setAppts] = useState([]);
  const [holidays, setHolidays] = useState({});
  const [selectedDay, setSelectedDay] = useState(todayIso());
  const [view, setView] = useState("calendar");

  const load = useCallback(async () => {
    const [p, a] = await Promise.all([api.get("/medico/profile"), api.get("/medico/appointments")]);
    setProfile(p.data.profile);
    setAppts(a.data.appointments);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const start = todayIso();
    const end = `${new Date().getFullYear() + 1}-12-31`;
    api.get("/holidays", { params: { start, end } }).then(({ data }) => {
      const map = {};
      data.holidays.forEach((h) => { map[h.date] = h.name; });
      setHolidays(map);
    });
  }, []);

  const apptsByDay = useMemo(() => {
    const m = {};
    appts.filter((a) => a.status === "confirmed").forEach((a) => {
      (m[a.date] = m[a.date] || []).push(a);
    });
    return m;
  }, [appts]);

  const vacation = profile?.vacation_days || [];
  const unlocked = profile?.unlocked_days || [];
  const blockedDays = profile?.admin_blocked_days || [];

  const getMarker = (d) => {
    if (apptsByDay[d]?.length) return "appointment";
    if (blockedDays.includes(d)) return "blocked";
    if (vacation.includes(d)) return "vacation";
    if (holidays[d] && !unlocked.includes(d)) return "holiday";
    const wd = (new Date(d + "T00:00:00").getDay() + 6) % 7;
    if (wd >= 5 && !unlocked.includes(d)) return "weekend";
    const slots = slotsForDate(profile, d);
    if (slots.length > 0) return "reception";
    return null;
  };

  const cancelAppt = async (id) => {
    try {
      await api.post(`/medico/appointments/${id}/cancel`);
      toast.success("Appuntamento disdetto");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadAgenda = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch (e) { toast.error("Errore durante l'esportazione"); }
  };

  const dayAppts = (apptsByDay[selectedDay] || []).sort((a, b) => a.time.localeCompare(b.time));
  const allConfirmed = appts.filter((a) => a.status === "confirmed").sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  return (
    <AppLayout title="Dashboard Medico" onOpenSettings={() => setSettingsOpen(true)}>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-800 text-slate-900">
            Benvenuto, Dr. {profile?.nome} {profile?.cognome}
          </h1>
          <p className="text-sm text-slate-500">{profile?.specializzazione} · Provincia di Como</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => downloadAgenda("/guides/medico", "Guida-Medico-Agenda-ISF-COMO.pdf")} data-testid="medico-guide-btn">
          <BookText className="h-4 w-4" /> Guida Medico (PDF)
        </Button>
      </div>

      <Tabs defaultValue="agenda">
        <TabsList>
          <TabsTrigger value="agenda" data-testid="tab-agenda"><CalendarDays className="mr-1.5 h-4 w-4" /> Agenda</TabsTrigger>
          <TabsTrigger value="disponibilita" data-testid="tab-disponibilita"><SlidersHorizontal className="mr-1.5 h-4 w-4" /> Disponibilità</TabsTrigger>
          <TabsTrigger value="ferie" data-testid="tab-ferie"><Plane className="mr-1.5 h-4 w-4" /> Ferie / Assenze</TabsTrigger>
        </TabsList>

        {/* AGENDA */}
        <TabsContent value="agenda" className="mt-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => downloadAgenda("/medico/agenda/pdf", "agenda-isf-como.pdf")} data-testid="export-pdf-btn">
                <FileDown className="h-4 w-4" /> Esporta PDF
              </Button>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => downloadAgenda("/medico/agenda/ics", "agenda-isf-como.ics")} data-testid="export-ics-btn">
                <CalendarDays className="h-4 w-4" /> Esporta ICS
              </Button>
            </div>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
              <button onClick={() => setView("calendar")} data-testid="view-calendar-btn"
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-500 transition-colors ${view === "calendar" ? "bg-blue-700 text-white" : "text-slate-600"}`}>
                <CalendarDays className="h-4 w-4" /> Calendario
              </button>
              <button onClick={() => setView("list")} data-testid="view-list-btn"
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-500 transition-colors ${view === "list" ? "bg-blue-700 text-white" : "text-slate-600"}`}>
                <List className="h-4 w-4" /> Lista
              </button>
            </div>
          </div>

          {view === "calendar" ? (
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <MonthCalendar value={selectedDay} onSelect={setSelectedDay} getMarker={getMarker} />
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="calendar-legend">
                  <span className="flex items-center gap-1.5 text-xs text-slate-600"><span className="h-3 w-3 rounded bg-sky-500" /> Giorni di ricevimento</span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-600"><span className="h-3 w-3 rounded bg-emerald-500" /> Con appuntamento</span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-600"><span className="h-3 w-3 rounded bg-rose-400" /> Sabato / Domenica</span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-600"><span className="h-3 w-3 rounded bg-amber-400" /> Assenza / ferie</span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-600"><span className="h-3 w-3 rounded bg-rose-600" /> Festività</span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-600"><span className="h-3 w-3 rounded bg-slate-800" /> Bloccato dall'amministratore</span>
                </div>
              </div>
              <div>
                <h3 className="font-heading text-base font-700 capitalize text-slate-900">{fmtDate(selectedDay)}</h3>
                <div className="mt-3 space-y-2">
                  {dayAppts.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">
                      Nessun appuntamento in questa data.
                    </p>
                  ) : dayAppts.map((a) => <ApptCard key={a.id} a={a} onCancel={cancelAppt} />)}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="agenda-list">
              {allConfirmed.length === 0 ? (
                <p className="rounded-lg border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">Nessun appuntamento confermato.</p>
              ) : allConfirmed.map((a) => (
                <ApptCard key={a.id} a={a} onCancel={cancelAppt} showDate />
              ))}
            </div>
          )}
        </TabsContent>

        {/* DISPONIBILITA */}
        <TabsContent value="disponibilita" className="mt-5">
          <AvailabilityEditor profile={profile} onSaved={load} />
          {profile && <div className="mt-6"><SchedulePeriods key={(profile.schedule_periods || []).length} profile={profile} onSaved={load} /></div>}
        </TabsContent>

        {/* FERIE */}
        <TabsContent value="ferie" className="mt-5">
          <VacationManager profile={profile} holidays={holidays} getMarker={getMarker} onSaved={load} />
        </TabsContent>
      </Tabs>

      <NotificationSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </AppLayout>
  );
}

function ApptCard({ a, onCancel, showDate }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4" data-testid={`appt-card-${a.id}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 flex-col items-center justify-center rounded-lg bg-sky-50 text-blue-700">
          <Clock className="h-4 w-4" />
          <span className="text-[11px] font-700">{a.time}</span>
        </div>
        <div>
          <p className="flex items-center gap-1.5 font-600 text-slate-900"><User className="h-3.5 w-3.5 text-slate-400" /> {a.isf_nome}</p>
          <p className="flex items-center gap-1.5 text-xs text-slate-500"><Building2 className="h-3 w-3" /> {a.isf_azienda}</p>
          {showDate && <p className="mt-0.5 text-xs text-slate-400 capitalize">{fmtDate(a.date)}</p>}
        </div>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600 hover:bg-red-50" data-testid={`cancel-appt-btn-${a.id}`}>
            <Ban className="mr-1 h-3.5 w-3.5" /> Disdici
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disdire l'appuntamento?</AlertDialogTitle>
            <AlertDialogDescription>
              L'informatore {a.isf_nome} riceverà una notifica di disdetta via email e in-app. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => onCancel(a.id)} data-testid={`confirm-cancel-${a.id}`}>Disdici</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AvailabilityEditor({ profile, onSaved }) {
  const [slots, setSlots] = useState({});
  const [max, setMax] = useState(2);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setSlots(profile.weekly_slots || {});
      setMax(profile.max_appointments_per_day || 2);
      setNote(profile.note || "");
    }
  }, [profile]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/medico/profile", {
        weekly_slots: slots, max_appointments_per_day: Number(max), note,
      });
      toast.success("Disponibilità aggiornata");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-heading font-700 text-slate-900">Orari settimanali standard</h3>
        <p className="mb-4 text-xs text-slate-500">Valgono sempre, salvo i periodi con orari diversi programmati qui sotto. Sabato e domenica sono disattivati di default.</p>
        <WeeklySlotsEditor value={slots} onChange={setSlots} />
      </div>
      <div className="space-y-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <Label htmlFor="max">Limite appuntamenti giornalieri</Label>
          <p className="mb-2 text-xs text-slate-500">Numero massimo di appuntamenti accettati in un singolo giorno.</p>
          <Input id="max" type="number" min={1} value={max} onChange={(e) => setMax(e.target.value)} className="w-28" data-testid="max-appts-input" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <Label htmlFor="note">Note e vincoli personalizzati</Label>
          <p className="mb-2 text-xs text-slate-500">Es. "Max 4 visite annue per azienda" o "Giovedì pomeriggio libero".</p>
          <Textarea id="note" value={note} onChange={(e) => setNote(e.target.value)} rows={4} data-testid="note-input" />
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-blue-700 hover:bg-blue-800" data-testid="save-availability-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Salva disponibilità</>}
        </Button>
      </div>
    </div>
  );
}

function VacationManager({ profile, getMarker, onSaved }) {
  const vacation = profile?.vacation_days || [];

  const toggle = async (dIso) => {
    try {
      if (vacation.includes(dIso)) {
        await api.delete(`/medico/vacation/${dIso}`);
        toast.success("Giorno di assenza rimosso");
      } else {
        const { data } = await api.post("/medico/vacation", { dates: [dIso] });
        toast.success(data.message);
      }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <h3 className="font-heading font-700 text-slate-900">Giorni di assenza / ferie</h3>
        <p className="mb-3 text-xs text-slate-500">Clicca un giorno per segnarlo come assenza. Gli appuntamenti già confermati in quel giorno verranno annullati automaticamente e l'ISF notificato.</p>
        <MonthCalendar
          getMarker={(d) => (vacation.includes(d) ? "vacation" : getMarker(d))}
          onSelect={toggle}
        />
      </div>
      <div>
        <h3 className="font-heading font-700 text-slate-900">Assenze programmate</h3>
        <div className="mt-3 space-y-2" data-testid="vacation-list">
          {vacation.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">Nessuna assenza programmata.</p>
          ) : [...vacation].sort().map((d) => (
            <div key={d} className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm">
              <span className="capitalize text-amber-800">{fmtDate(d)}</span>
              <button onClick={() => toggle(d)} className="rounded-full p-1 text-amber-600 hover:bg-amber-100" data-testid={`remove-vacation-${d}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
